import React from 'react';
import { Button } from '../components/ui/button';
import { Check, Calendar, TrendingUp, Target, Shield, Zap } from 'lucide-react';

const AngebotPage = () => {
  const calendlyUrl = "https://calendly.com/simeon-valoryxsdr/30min";

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 py-6 px-6 lg:px-12">
        <div className="container mx-auto">
          <img 
            src="/valoryx-logo.png" 
            alt="VALORYX" 
            className="h-10"
          />
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-blue-50 rounded-full mb-6">
              <span className="text-sm font-semibold text-blue-600">PILOT-PROGRAMM</span>
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
              Qualifizierte Termine.<br />
              <span className="text-gray-600">Planbar und messbar.</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir vereinbaren hochwertige Erstgespräche mit Entscheidern aus Ihrer Zielgruppe. Sie zahlen nur für Ergebnisse.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 px-6 lg:px-12 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl p-8 md:p-12 border-2 border-gray-900 shadow-xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Transparentes Preismodell</h2>
              <p className="text-gray-600">Kombiniert minimales Risiko mit maximaler Erfolgsorientierung</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Fix Component */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <Shield className="w-6 h-6 text-gray-900 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">Monatliche Basis</h3>
                </div>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gray-900">€1.000</span>
                  <span className="text-gray-600 ml-2">/Monat</span>
                </div>
                <p className="text-gray-600 text-sm">
                  Deckt Infrastruktur, Recherche und Grundkosten ab. Monatlich kündbar.
                </p>
              </div>

              {/* Variable Component */}
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <div className="flex items-center mb-4">
                  <Target className="w-6 h-6 text-blue-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">Pro qualifiziertem Termin</h3>
                </div>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-blue-600">€450</span>
                  <span className="text-gray-600 ml-2">/Termin</span>
                </div>
                <p className="text-gray-600 text-sm">
                  Nur bei Terminen, die stattfinden und Ihre Qualitätskriterien erfüllen.
                </p>
              </div>
            </div>

            {/* Example Calculation */}
            <div className="bg-gray-900 text-white rounded-xl p-6">
              <h4 className="font-bold mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Beispielrechnung
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Monatliche Basis</span>
                  <span className="font-semibold">€1.000</span>
                </div>
                <div className="flex justify-between">
                  <span>12 qualifizierte Termine × €450</span>
                  <span className="font-semibold">€5.400</span>
                </div>
                <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between text-lg">
                  <span className="font-bold">Gesamt</span>
                  <span className="font-bold">€6.400</span>
                </div>
              </div>
              <p className="text-gray-400 text-xs mt-3">
                *Beispiel bei 12 Terminen pro Monat
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Was ist inkludiert?</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center mr-4">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Multichannel-Akquise</h3>
                <p className="text-gray-600">E-Mail, LinkedIn und Telefon – wir nutzen die Kanäle, die für Ihre Zielgruppe am besten funktionieren.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center mr-4">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">ICP-Definition & Targeting</h3>
                <p className="text-gray-600">Gemeinsame Festlegung Ihres Ideal Customer Profile und präzise Zielgruppenansprache.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center mr-4">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Transparentes Reporting</h3>
                <p className="text-gray-600">Wöchentliche Updates zu Aktivitäten, Kennzahlen und vereinbarten Terminen.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center mr-4">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Qualitätssicherung</h3>
                <p className="text-gray-600">Nur Termine, die Ihre definierten Kriterien erfüllen und tatsächlich stattfinden, werden berechnet.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center mr-4">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">CRM-Integration</h3>
                <p className="text-gray-600">Nahtlose Anbindung an Ihr bestehendes CRM-System für automatisierte Prozesse.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center mr-4">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Monatlich kündbar</h3>
                <p className="text-gray-600">Keine langfristige Bindung. Sie können jederzeit mit einer Frist von 30 Tagen kündigen.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualification Criteria */}
      <section className="py-16 px-6 lg:px-12 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Was ist ein qualifizierter Termin?</h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Transparente Kriterien für die Abrechnung
          </p>

          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <div className="space-y-6">
              <div className="flex items-start">
                <Zap className="w-6 h-6 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Der Termin findet tatsächlich statt</h3>
                  <p className="text-gray-600">No-Shows werden nicht berechnet. Wir tragen das Risiko gemeinsam mit Ihnen.</p>
                </div>
              </div>

              <div className="flex items-start">
                <Zap className="w-6 h-6 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Entscheider ist anwesend</h3>
                  <p className="text-gray-600">Der Gesprächspartner hat Budgetverantwortung oder maßgeblichen Einfluss auf die Kaufentscheidung.</p>
                </div>
              </div>

              <div className="flex items-start">
                <Zap className="w-6 h-6 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">ICP-Match ist gegeben</h3>
                  <p className="text-gray-600">Das Unternehmen erfüllt Ihre vorab definierten Kriterien (Branche, Größe, Budget, etc.).</p>
                </div>
              </div>

              <div className="flex items-start">
                <Zap className="w-6 h-6 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Mindestdauer von 15 Minuten</h3>
                  <p className="text-gray-600">Das Gespräch dauert mindestens 15 Minuten, um eine sinnvolle Qualifizierung zu ermöglichen.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Works */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Warum dieses Modell?</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Minimales Risiko</h3>
              <p className="text-gray-600">
                Kleine Vorauszahlung deckt nur Grundkosten. Kein Minimum, keine Vertragsbindung.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Volle Transparenz</h3>
              <p className="text-gray-600">
                Sie sehen jeden Kontakt, jede Aktivität und zahlen nur für qualifizierte Ergebnisse.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Skalierbar</h3>
              <p className="text-gray-600">
                Start mit wenigen Terminen möglich. Skalierung nach Bedarf und Budget.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-12 bg-gray-900 text-white">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-4xl font-bold mb-6">
            Bereit für planbare Pipeline?
          </h2>
          <p className="text-xl text-gray-300">
            Lassen Sie uns im Gespräch die Details finalisieren und direkt starten.
          </p>
        </div>
      </section>

      {/* Logos Section */}
      <section className="py-16 px-6 lg:px-12 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                Vertrauen von Unternehmen, die langfristig denken
              </p>
            </div>
            
            {/* Logo Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
              <div className="flex items-center justify-center p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors duration-200">
                <div className="w-full h-16 flex items-center justify-center">
                  <img 
                    src="/client-logo-1.png" 
                    alt="LeadEffect"
                    className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-center p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors duration-200">
                <div className="w-full h-16 flex items-center justify-center">
                  <img 
                    src="/client-logo-2.png" 
                    alt="Finanzmarkt"
                    className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-center p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors duration-200">
                <div className="w-full h-20 flex items-center justify-center">
                  <img 
                    src="/saxenhammer.png" 
                    alt="Saxenhammer & Co."
                    className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-center p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors duration-200">
                <div className="w-full h-16 flex items-center justify-center">
                  <img 
                    src="/client-logo-4.png" 
                    alt="RemoteClip"
                    className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-center p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors duration-200">
                <div className="w-full h-16 flex items-center justify-center">
                  <img 
                    src="/client-logo-5.jpeg" 
                    alt="GU Energy Group"
                    className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-center p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors duration-200">
                <div className="w-full h-20 flex items-center justify-center">
                  <img 
                    src="/rigilog.jpg" 
                    alt="Rigilog AG"
                    className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 px-6 lg:px-12">
        <div className="container mx-auto text-center text-gray-600 text-sm">
          <p>© 2025 VALORYX · Charlottenstraße 85, 10969 Berlin · info@valoryx.de</p>
        </div>
      </footer>
    </div>
  );
};

export default AngebotPage;
